import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import SearchableDataTable from '../components/SearchableDataTable';
import '../styles/EntityManagement.css';

const outcomeColors = {
  success: 'success',
  failure: 'error'
};

const formatDateTime = (value) => (
  value ? new Date(value).toLocaleString() : '-'
);

const formatActor = (row) => {
  if (row.actor) {
    return `${row.actor.firstName} ${row.actor.lastName}`;
  }

  return row.actorEmail || 'System';
};

const formatTarget = (row) => {
  if (!row.targetType && !row.targetId) return '-';
  return [row.targetType, row.targetId].filter(Boolean).join(' ');
};

const formatSite = (row) => {
  if (row.site) {
    return row.site.companyName || row.site.slug;
  }

  const metadata = row.metadata || {};
  const archivedLabel = metadata.companyName || metadata.slug;
  if (archivedLabel) {
    return `${archivedLabel} (deleted)`;
  }

  return '-';
};

const AuditLog = () => {
  const { site } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    action: '',
    outcome: '',
    siteId: '',
    targetType: ''
  });
  const [selectedLog, setSelectedLog] = useState(null);

  const loadAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value)
      );
      const response = await api.get('/audit-logs', { params });
      setAuditLogs(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (site?.isGlobal) {
      loadAuditLogs();
    }
  }, [loadAuditLogs, site]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      action: '',
      outcome: '',
      siteId: '',
      targetType: ''
    });
  };

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Time',
      flex: 1,
      valueFormatter: (params) => formatDateTime(params.value)
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1.1
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={outcomeColors[params.value] || 'default'}
          variant={params.value === 'success' ? 'filled' : 'outlined'}
        />
      )
    },
    {
      field: 'actorEmail',
      headerName: 'Actor',
      flex: 1,
      valueGetter: (params) => formatActor(params.row)
    },
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => formatSite(params.row),
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'target',
      headerName: 'Target',
      flex: 1.15,
      minWidth: 180,
      valueGetter: (params) => formatTarget(params.row),
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'ipAddress',
      headerName: 'IP',
      flex: 0.8
    },
    {
      field: 'details',
      headerName: 'Details',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Button size="small" onClick={() => setSelectedLog(params.row)}>
          View
        </Button>
      )
    }
  ];

  if (!site?.isGlobal) {
    return (
      <Box className="entity-management-container">
        <Box className="toolbar-container">
          <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>
            Audit
          </Typography>
        </Box>
        <Typography variant="body2">Audit logs are only available on the main admin site.</Typography>
      </Box>
    );
  }

  return (
    <Box className="entity-management-container">
      <Box className="toolbar-container" sx={{ alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" className="page-title" sx={{ pl: 0, mb: 1.5, fontWeight: 'bold' }}>
            Audit
          </Typography>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ maxWidth: 860 }}
          >
            <TextField
              select
              name="action"
              label="Action"
              value={filters.action}
              onChange={handleFilterChange}
              fullWidth
              size="small"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="auth.">Auth</MenuItem>
              <MenuItem value="site.">Sites</MenuItem>
              <MenuItem value="site.ec2">EC2</MenuItem>
              <MenuItem value="site.deploy">Deploys</MenuItem>
              <MenuItem value="user.">Users</MenuItem>
              <MenuItem value="user.invite">Invites</MenuItem>
            </TextField>
            <TextField
              select
              name="outcome"
              label="Outcome"
              value={filters.outcome}
              onChange={handleFilterChange}
              fullWidth
              size="small"
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Outcomes</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Failure</MenuItem>
            </TextField>
            <TextField
              select
              name="targetType"
              label="Target Type"
              value={filters.targetType}
              onChange={handleFilterChange}
              fullWidth
              size="small"
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">All Target Types</MenuItem>
              <MenuItem value="site">Site</MenuItem>
              <MenuItem value="user">User</MenuItem>
            </TextField>
            <Button
              onClick={handleClearFilters}
              variant="outlined"
              sx={{
                height: '40px',
                minWidth: 90,
                borderColor: 'var(--color-primary)',
                color: 'var(--color-black)',
                flexShrink: 0
              }}
            >
              Clear
            </Button>
          </Stack>
        </Box>

        <Box className="button-group" sx={{ pt: 0.25 }}>
          <Button
            className="action-button"
            startIcon={<RefreshIcon />}
            onClick={loadAuditLogs}
            disabled={loading}
            sx={{
              textTransform: 'uppercase',
              fontWeight: 700,
              minWidth: 110,
              width: 'fit-content',
              padding: '6px 16px',
              border: '2px solid var(--color-primary)',
              color: 'var(--color-black)'
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box className="main-content">
        <Box className="table-container">
          <SearchableDataTable
            searchPlaceholder="Search Audit Logs..."
            rows={auditLogs}
            columns={columns}
            loading={loading}
            tableName="audit-logs"
            height="100%"
          />
        </Box>
      </Box>

      <Dialog open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Time</Typography>
                <Typography variant="body2">{formatDateTime(selectedLog.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Action</Typography>
                <Typography variant="body2">{selectedLog.action}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Actor</Typography>
                <Typography variant="body2">{formatActor(selectedLog)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Outcome</Typography>
                <Typography variant="body2">{selectedLog.outcome}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Metadata</Typography>
                <Box component="pre" sx={{
                  bgcolor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  p: 1,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap'
                }}>
                  {JSON.stringify(selectedLog.metadata || {}, null, 2)}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLog;
